// Excel Template Generator for Bulk Onboarding
// This utility creates an Excel file with dropdown validation for bulk onboarding

import * as XLSX from 'xlsx';

// Available options for dropdowns
const DROPDOWN_OPTIONS = {
  gender: ['Male', 'Female', 'Other'],
  bloodGroup: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'],
  roleName: [
    'Driver',
    'Security Officer',
    'Supervisor',
    'Manager',
    'Admin',
    'Field Officer',
    'Patrol Officer',
    'Site Manager',
    'Operations Manager',
  ],
  areaAvailable: [
    'North Zone',
    'South Zone',
    'East Zone',
    'West Zone',
    'Central Zone',
    'All Zones',
  ],
  commercialLicense: ['Yes', 'No'],
  internationalDrivingPermit: ['Yes', 'No'],
  fourWheeler: ['Yes', 'No'],
  heavyMotor: ['Yes', 'No'],
};

// Sample data for the template
const SAMPLE_DATA = [
  {
    'Employee ID': 'EMP001',
    'Full Name': 'John Doe',
    'Date of Birth': '1990-01-01',
    'Phone Number': '+911234567890',
    Gender: 'Male',
    'Blood Group': 'O+',
    Address: '123 Main Street, City',
    'Reports To': 'EMP0000',
    'Area Available': 'North Zone',
    'Role Name': 'Driver',
    'Commercial License': 'Yes',
    'International Driving Permit': 'No',
    '4 Wheeler': 'Yes',
    'Heavy Motor': 'No',
  },
  {
    'Employee ID': 'EMP002',
    'Full Name': 'Jane Smith',
    'Date of Birth': '1985-05-15',
    'Phone Number': '+911234567891',
    Gender: 'Female',
    'Blood Group': 'A-',
    Address: '456 Oak Avenue, City',
    'Reports To': 'EMP0000',
    'Area Available': 'South Zone',
    'Role Name': 'Manager',
    'Commercial License': '',
    'International Driving Permit': '',
    '4 Wheeler': '',
    'Heavy Motor': '',
  },
];

/**
 * Generate Excel template with dropdown validation
 * @param {Array} roles - Available roles from API
 * @param {Array} supervisors - Available supervisors for "Reports To" field
 * @returns {Blob} Excel file blob
 */
export const generateExcelTemplate = (roles = [], supervisors = []) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Prepare the data
  const headers = [
    'Employee ID',
    'Full Name',
    'Date of Birth',
    'Phone Number',
    'Gender',
    'Blood Group',
    'Address',
    'Reports To',
    'Area Available',
    'Role Name',
    'Commercial License',
    'International Driving Permit',
    '4 Wheeler',
    'Heavy Motor',
  ];

  // Update role options if provided
  if (roles.length > 0) {
    DROPDOWN_OPTIONS.roleName = roles.map(
      role => role.role_name || role.display_name || role
    );
  } else {
    // Use fallback roles if none provided
    DROPDOWN_OPTIONS.roleName = [
      'Driver',
      'Security Officer',
      'Supervisor',
      'Manager',
      'Admin',
      'Field Officer',
      'Patrol Officer',
      'Site Manager',
      'Operations Manager',
    ];
  }

  // Update supervisor options if provided
  const reportsToOptions =
    supervisors.length > 0
      ? supervisors.map(sup => sup.employee_id || sup.id || sup)
      : ['EMP0000', 'EMP0001', 'EMP0002'];

  // Create worksheet data with proper alignment
  const worksheetData = [
    headers,
    ...SAMPLE_DATA.map(row => {
      const alignedRow = [];
      headers.forEach(header => {
        const value = row[header] || '';
        // Ensure phone numbers are formatted as text
        if (header === 'Phone Number' && value) {
          alignedRow.push(`'${value}`); // Prefix with ' to force text format
        } else {
          alignedRow.push(value);
        }
      });
      return alignedRow;
    }),
  ];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  const columnWidths = [
    { wch: 12 }, // Employee ID
    { wch: 20 }, // Full Name
    { wch: 12 }, // Date of Birth
    { wch: 15 }, // Phone Number
    { wch: 8 }, // Gender
    { wch: 10 }, // Blood Group
    { wch: 30 }, // Address
    { wch: 12 }, // Reports To
    { wch: 15 }, // Area Available
    { wch: 15 }, // Role Name
    { wch: 15 }, // Commercial License
    { wch: 25 }, // International Driving Permit
    { wch: 10 }, // 4 Wheeler
    { wch: 12 }, // Heavy Motor
  ];
  worksheet['!cols'] = columnWidths;

  // Add data validation for dropdowns
  const validationRules = [
    {
      range: 'E2:E1000', // Gender column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.gender.join(',')}"`,
    },
    {
      range: 'F2:F1000', // Blood Group column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.bloodGroup.join(',')}"`,
    },
    {
      range: 'H2:H1000', // Reports To column
      type: 'list',
      formula: `"${reportsToOptions.join(',')}"`,
    },
    {
      range: 'I2:I1000', // Area Available column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.areaAvailable.join(',')}"`,
    },
    {
      range: 'J2:J1000', // Role Name column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.roleName.join(',')}"`,
    },
    {
      range: 'K2:K1000', // Commercial License column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.commercialLicense.join(',')}"`,
    },
    {
      range: 'L2:L1000', // International Driving Permit column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.internationalDrivingPermit.join(',')}"`,
    },
    {
      range: 'M2:M1000', // 4 Wheeler column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.fourWheeler.join(',')}"`,
    },
    {
      range: 'N2:N1000', // Heavy Motor column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.heavyMotor.join(',')}"`,
    },
  ];

  // Add cell formatting for better display
  const cellFormats = {
    'D2:D1000': {
      // Phone Number column
      t: 's', // Text format to prevent scientific notation
    },
    'A2:A1000': {
      // Employee ID column
      t: 's', // Text format
    },
  };

  // Apply cell formats
  Object.keys(cellFormats).forEach(range => {
    const [start, end] = range.split(':');
    const startCell = XLSX.utils.decode_cell(start);
    const endCell = XLSX.utils.decode_cell(end);

    for (let row = startCell.r; row <= endCell.r; row++) {
      for (let col = startCell.c; col <= endCell.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: '' };
        worksheet[cellAddress].t = cellFormats[range].t;
      }
    }
  });

  // Add validation rules to worksheet
  if (worksheet['!dataValidation']) {
    worksheet['!dataValidation'].push(...validationRules);
  } else {
    worksheet['!dataValidation'] = validationRules;
  }

  // Add instructions sheet
  const instructionsData = [
    ['BULK ONBOARDING TEMPLATE INSTRUCTIONS'],
    [''],
    ['REQUIRED FIELDS:'],
    ['• Employee ID: Unique identifier (e.g., EMP001)'],
    ['• Full Name: Complete name of the employee'],
    ['• Date of Birth: Format YYYY-MM-DD or DD-MM-YYYY'],
    ['• Phone Number: Include country code (e.g., +911234567890)'],
    ['• Gender: Select from dropdown'],
    ['• Blood Group: Select from dropdown'],
    ['• Address: Complete address'],
    ['• Reports To: Select supervisor from dropdown'],
    ['• Area Available: Select work area from dropdown'],
    ['• Role Name: Select role from dropdown'],
    [''],
    ['OPTIONAL FIELDS (for Driver role):'],
    ['• Commercial License: Yes/No'],
    ['• International Driving Permit: Yes/No'],
    ['• 4 Wheeler: Yes/No'],
    ['• Heavy Motor: Yes/No'],
    [''],
    ['IMPORTANT NOTES:'],
    ['• Use the dropdown menus for consistent data entry'],
    ['• Employee IDs must be unique'],
    ['• Date format: YYYY-MM-DD (recommended) or DD-MM-YYYY'],
    ['• Phone numbers should include country code'],
    ['• Driver-specific fields are only required for Driver role'],
    ['• Save as Excel (.xlsx) format before uploading'],
    [''],
    ['DATA FORMAT GUIDELINES:'],
    ['• Employee ID: Must start with "EMP" followed by numbers (e.g., EMP001)'],
    ['• Phone Number: Include country code with + sign (e.g., +911234567890)'],
    ['• Date of Birth: Use YYYY-MM-DD format (e.g., 1990-01-01)'],
    ['• Reports To: Must be a valid Employee ID from supervisor list'],
    ['• Area Available: Select from dropdown list only'],
    ['• Role Name: Select from dropdown list only'],
    ['• Driver fields: Only fill if Role Name is "Driver"'],
    [''],
    ['TROUBLESHOOTING:'],
    ['• If dropdowns are empty: Check your internet connection'],
    ['• If template download fails: Refresh the page and try again'],
    ['• If upload fails: Check network status indicator'],
    ['• For network issues: Use the Retry button'],
    ['• If roles are missing: Contact your administrator'],
    ['• Phone numbers in scientific notation: Format as text with + sign'],
  ];

  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
  instructionsSheet['!cols'] = [{ wch: 50 }];

  // Add sheets to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Data');
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Generate a clean Excel template without sample data
 * @param {Array} roles - Available roles from API
 * @param {Array} supervisors - Available supervisors
 * @returns {Blob} Excel file blob
 */
export const generateCleanExcelTemplate = (roles = [], supervisors = []) => {
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Prepare the data
  const headers = [
    'Employee ID',
    'Full Name',
    'Date of Birth',
    'Phone Number',
    'Gender',
    'Blood Group',
    'Address',
    'Reports To',
    'Area Available',
    'Role Name',
    'Commercial License',
    'International Driving Permit',
    '4 Wheeler',
    'Heavy Motor',
  ];

  // Update role options if provided
  if (roles.length > 0) {
    DROPDOWN_OPTIONS.roleName = roles.map(
      role => role.role_name || role.display_name || role
    );
  } else {
    // Use fallback roles if none provided
    DROPDOWN_OPTIONS.roleName = [
      'Driver',
      'Security Officer',
      'Supervisor',
      'Manager',
      'Admin',
      'Field Officer',
      'Patrol Officer',
      'Site Manager',
      'Operations Manager',
    ];
  }

  // Update supervisor options if provided
  const reportsToOptions =
    supervisors.length > 0
      ? supervisors.map(sup => sup.employee_id || sup.id || sup)
      : ['EMP0000', 'EMP0001', 'EMP0002'];

  // Create worksheet data with only headers
  const worksheetData = [headers];

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths
  const columnWidths = [
    { wch: 12 }, // Employee ID
    { wch: 20 }, // Full Name
    { wch: 12 }, // Date of Birth
    { wch: 15 }, // Phone Number
    { wch: 8 }, // Gender
    { wch: 10 }, // Blood Group
    { wch: 30 }, // Address
    { wch: 12 }, // Reports To
    { wch: 15 }, // Area Available
    { wch: 15 }, // Role Name
    { wch: 15 }, // Commercial License
    { wch: 25 }, // International Driving Permit
    { wch: 10 }, // 4 Wheeler
    { wch: 12 }, // Heavy Motor
  ];
  worksheet['!cols'] = columnWidths;

  // Add data validation for dropdowns (same as before)
  const validationRules = [
    {
      range: 'E2:E1000', // Gender column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.gender.join(',')}"`,
    },
    {
      range: 'F2:F1000', // Blood Group column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.bloodGroup.join(',')}"`,
    },
    {
      range: 'H2:H1000', // Reports To column
      type: 'list',
      formula: `"${reportsToOptions.join(',')}"`,
    },
    {
      range: 'I2:I1000', // Area Available column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.areaAvailable.join(',')}"`,
    },
    {
      range: 'J2:J1000', // Role Name column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.roleName.join(',')}"`,
    },
    {
      range: 'K2:K1000', // Commercial License column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.commercialLicense.join(',')}"`,
    },
    {
      range: 'L2:L1000', // International Driving Permit column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.internationalDrivingPermit.join(',')}"`,
    },
    {
      range: 'M2:M1000', // 4 Wheeler column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.fourWheeler.join(',')}"`,
    },
    {
      range: 'N2:N1000', // Heavy Motor column
      type: 'list',
      formula: `"${DROPDOWN_OPTIONS.heavyMotor.join(',')}"`,
    },
  ];

  // Add validation rules to worksheet
  if (worksheet['!dataValidation']) {
    worksheet['!dataValidation'].push(...validationRules);
  } else {
    worksheet['!dataValidation'] = validationRules;
  }

  // Add instructions sheet (same as before)
  const instructionsData = [
    ['BULK ONBOARDING TEMPLATE INSTRUCTIONS'],
    [''],
    ['REQUIRED FIELDS:'],
    ['• Employee ID: Unique identifier (e.g., EMP001)'],
    ['• Full Name: Complete name of the employee'],
    ['• Date of Birth: Format YYYY-MM-DD or DD-MM-YYYY'],
    ['• Phone Number: Include country code (e.g., +911234567890)'],
    ['• Gender: Select from dropdown'],
    ['• Blood Group: Select from dropdown'],
    ['• Address: Complete address'],
    ['• Reports To: Select supervisor from dropdown'],
    ['• Area Available: Select work area from dropdown'],
    ['• Role Name: Select role from dropdown'],
    [''],
    ['OPTIONAL FIELDS (for Driver role):'],
    ['• Commercial License: Yes/No'],
    ['• International Driving Permit: Yes/No'],
    ['• 4 Wheeler: Yes/No'],
    ['• Heavy Motor: Yes/No'],
    [''],
    ['IMPORTANT NOTES:'],
    ['• Use the dropdown menus for consistent data entry'],
    ['• Employee IDs must be unique'],
    ['• Date format: YYYY-MM-DD (recommended) or DD-MM-YYYY'],
    ['• Phone numbers should include country code'],
    ['• Driver-specific fields are only required for Driver role'],
    ['• Save as Excel (.xlsx) format before uploading'],
    [''],
    ['DATA FORMAT GUIDELINES:'],
    ['• Employee ID: Must start with "EMP" followed by numbers (e.g., EMP001)'],
    ['• Phone Number: Include country code with + sign (e.g., +911234567890)'],
    ['• Date of Birth: Use YYYY-MM-DD format (e.g., 1990-01-01)'],
    ['• Reports To: Must be a valid Employee ID from supervisor list'],
    ['• Area Available: Select from dropdown list only'],
    ['• Role Name: Select from dropdown list only'],
    ['• Driver fields: Only fill if Role Name is "Driver"'],
    [''],
    ['TROUBLESHOOTING:'],
    ['• If dropdowns are empty: Check your internet connection'],
    ['• If template download fails: Refresh the page and try again'],
    ['• If upload fails: Check network status indicator'],
    ['• For network issues: Use the Retry button'],
    ['• If roles are missing: Contact your administrator'],
    ['• Phone numbers in scientific notation: Format as text with + sign'],
  ];

  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
  instructionsSheet['!cols'] = [{ wch: 50 }];

  // Add sheets to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee Data');
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    cellStyles: true,
  });

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/**
 * Download the Excel template
 * @param {Array} roles - Available roles from API
 * @param {Array} supervisors - Available supervisors
 */
export const downloadExcelTemplate = (roles = [], supervisors = []) => {
  try {
    const excelBlob = generateExcelTemplate(roles, supervisors);
    const url = window.URL.createObjectURL(excelBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_onboarding_template_enhanced.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    console.log('✅ Excel template downloaded successfully');
  } catch (error) {
    console.error('❌ Error downloading Excel template:', error);
    throw new Error('Failed to generate Excel template');
  }
};

/**
 * Process uploaded Excel file and extract data
 * @param {File} file - Excel file
 * @returns {Promise<Array>} Array of employee data
 */
export const processExcelFile = async file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get the first worksheet (Employee Data)
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
        });

        if (jsonData.length < 2) {
          throw new Error('No data found in Excel file');
        }

        // Extract headers and data
        const headers = jsonData[0];
        const rows = jsonData.slice(1);

        // Convert to object array
        const employeeData = rows
          .filter(row => row.some(cell => cell !== '')) // Remove empty rows
          .map(row => {
            const employee = {};
            headers.forEach((header, index) => {
              employee[header] = row[index] || '';
            });
            return employee;
          });

        resolve(employeeData);
      } catch (error) {
        reject(new Error(`Failed to process Excel file: ${error.message}`));
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Validate Excel data structure
 * @param {Array} data - Employee data array
 * @returns {Object} Validation result
 */
export const validateExcelData = data => {
  const errors = [];
  const warnings = [];

  if (!Array.isArray(data) || data.length === 0) {
    errors.push('No data found in Excel file');
    return { isValid: false, errors, warnings };
  }

  const requiredFields = [
    'Employee ID',
    'Full Name',
    'Date of Birth',
    'Phone Number',
    'Gender',
    'Blood Group',
    'Address',
    'Reports To',
    'Area Available',
    'Role Name',
  ];

  // Check if all required fields are present
  const headers = Object.keys(data[0] || {});
  const missingFields = requiredFields.filter(
    field => !headers.includes(field)
  );

  if (missingFields.length > 0) {
    errors.push(`Missing required columns: ${missingFields.join(', ')}`);
  }

  // Check for duplicate Employee IDs
  const employeeIds = data.map(row => row['Employee ID']).filter(id => id);
  const duplicateIds = employeeIds.filter(
    (id, index) => employeeIds.indexOf(id) !== index
  );

  if (duplicateIds.length > 0) {
    errors.push(
      `Duplicate Employee IDs found: ${[...new Set(duplicateIds)].join(', ')}`
    );
  }

  // Check data quality
  data.forEach((row, index) => {
    const rowNum = index + 2; // +2 for header and 1-based indexing

    requiredFields.forEach(field => {
      if (!row[field] || row[field].toString().trim() === '') {
        warnings.push(`Row ${rowNum}: ${field} is empty`);
      }
    });

    // Validate date format
    if (row['Date of Birth']) {
      const dateStr = row['Date of Birth'].toString();
      const yyyyMMddRegex = /^\d{4}-\d{2}-\d{2}$/;
      const ddMMyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;

      if (!yyyyMMddRegex.test(dateStr) && !ddMMyyyyRegex.test(dateStr)) {
        warnings.push(
          `Row ${rowNum}: Date of Birth format should be YYYY-MM-DD or DD-MM-YYYY`
        );
      }
    }

    // Validate phone number
    if (row['Phone Number'] && !/^\+?[\d\s\-()]+$/.test(row['Phone Number'])) {
      warnings.push(`Row ${rowNum}: Phone Number format appears invalid`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    totalRows: data.length,
    validRows: data.length - warnings.length,
  };
};

export default {
  generateExcelTemplate,
  downloadExcelTemplate,
  processExcelFile,
  validateExcelData,
  DROPDOWN_OPTIONS,
};
